import {
  faBars,
  faGlobe,
  faHouse,
  faSearch,
  faUser,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

import { languageFlags } from "../consts/flags";
import { languageCodes } from "../consts/language-codes";
import { useLocale } from "../stores/locale";
import { Container } from "./container";
import { Dropdown } from "./dropdown";
import { SafeImage } from "./safe-image";
import { TreeMenu } from "./tree-menu";

export type Menu = {
  slug: string;
  title?: string;
  children?: MenuItem[];
  redirectTo?: string;
};
export type MenuSeparator = {
  separator: true;
};

export type MenuItem = Menu | MenuSeparator;

export const colors = {
  pink: {
    background: "bg-pink-500",
    active: "bg-pink-600",
    hover: "hover:bg-pink-600",
    text: "text-white",
  },
  blue: {
    background: "bg-blue-500",
    active: "bg-blue-600",
    hover: "hover:bg-blue-600",
    text: "text-white",
  },
};

export function Navbar({
  menu,
  profileMenu,
  color,
  name,
  logoImgUrl,
  availableLanguages,
  onLanguageChange,
  showSearch = true,
  searchValue,
  onSearch,
}: {
  menu: MenuItem[];
  profileMenu?: Menu[];
  color: keyof typeof colors;
  name: string;
  logoImgUrl?: string;
  availableLanguages?: string[];
  onLanguageChange?: (language: string) => void;
  showSearch?: boolean;
  searchValue?: string;
  onSearch?: (query: string) => void;
}) {
  const location = useLocation();
  const { locale: t } = useLocale();

  const [open, setOpen] = useState(false);

  const prevKeyRef = useRef(location.key);

  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    setSearchQuery(searchValue ?? "");
  }, [searchValue]);

  // Close drawer on route change
  useEffect(() => {
    if (prevKeyRef.current !== location.key) {
      prevKeyRef.current = location.key;
      setOpen(false);
    }
  }, [location]);
  return (
    <div
      className={`w-full ${open ? `${colors[color].background} md:bg-inherit h-dvh md:h-auto` : ""}`}
    >
      <Container className="hidden md:flex justify-between items-center">
        <Logo className="h-[60px] p-3" logoImgUrl={logoImgUrl} name={name} />
        <div className="mr-4 flex gap-2">
          {availableLanguages && (
            <Dropdown
              buttonText={true}
              itemClassName={`px-2 py-1 text-white ${colors[color].hover}`}
              label={<FontAwesomeIcon icon={faGlobe} />}
              listClassName={`${colors[color].background} whitespace-nowrap rounded-sm border-1 border-black`}
              options={availableLanguages.map((language) => ({
                label: `${languageFlags[language as keyof typeof languageFlags]} ${t.locale[languageCodes[language as keyof typeof languageCodes]]}`,
                value: language,
              }))}
              onSelect={(language) => onLanguageChange?.(language)}
            />
          )}
          {profileMenu && (
            <Dropdown
              buttonText={true}
              itemClassName={`px-2 py-1 text-center ${colors[color].hover}`}
              label={<FontAwesomeIcon icon={faUser} />}
              listClassName={`${colors[color].background} whitespace-nowrap rounded-sm border-1 border-black`}
              options={profileMenu.map((menuItem) => ({
                label: menuItem.title || menuItem.slug,
                route: menuItem.redirectTo || menuItem.slug,
              }))}
              onSelect={(language) => onLanguageChange?.(language)}
            />
          )}
        </div>
      </Container>
      <div className={`h-[50px] flex ${colors[color].background}`}>
        <Container className="flex">
          <Logo
            className={`flex md:hidden p-5 ${colors[color].text}`}
            name={name}
          />
          <ul className="hidden md:flex flex-1 h-full items-center">
            {menu &&
              menu.map((menuItem, index) => (
                <li
                  key={`menu-${index}`}
                  className={`h-full flex items-center ${
                    "separator" in menuItem ? "flex-1" : ""
                  }`}
                >
                  {!("separator" in menuItem) && (
                    <NavLink
                      className={`px-5 py-3 ${colors[color].text} ${colors[color].hover} ${isActive(menuItem.slug, location.pathname) ? colors[color].active : ""}`}
                      to={menuItem.redirectTo || menuItem.slug}
                    >
                      {menuItem.title || menuItem.slug}
                    </NavLink>
                  )}
                </li>
              ))}
          </ul>
          <div className="flex-1 md:flex-none flex justify-end h-full items-center pr-4">
            {showSearch && (
              <form
                className="relative w-full h-full py-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  onSearch?.(searchQuery);
                }}
              >
                <FontAwesomeIcon
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500"
                  icon={faSearch}
                />
                <input
                  className="w-full h-full bg-white rounded-sm pl-8"
                  name="query"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </form>
            )}
            <div className="md:hidden flex mx-2 gap-2 flex-nowrap">
              {availableLanguages && (
                <Dropdown
                  buttonText={true}
                  itemClassName={`px-2 py-1 text-center hover:bg-gray-200 ${colors[color].hover}`}
                  label={<FontAwesomeIcon icon={faGlobe} />}
                  listClassName={`${colors[color].background} ${colors[color].text} whitespace-nowrap rounded-b-sm`}
                  options={availableLanguages.map((language) => ({
                    label: `${languageFlags[language as keyof typeof languageFlags]} ${t.locale[languageCodes[language as keyof typeof languageCodes]]}`,
                    value: language,
                  }))}
                  onSelect={(language) => onLanguageChange?.(language)}
                />
              )}
              {profileMenu && (
                <Dropdown
                  buttonText={true}
                  itemClassName={`px-2 py-1 text-center hover:bg-gray-200 ${colors[color].hover}`}
                  label={<FontAwesomeIcon icon={faUser} />}
                  listClassName={`${colors[color].background} ${colors[color].text} whitespace-nowrap rounded-b-sm`}
                  options={profileMenu.map((menuItem) => ({
                    label: menuItem.title || menuItem.slug,
                    route: menuItem.redirectTo || menuItem.slug,
                  }))}
                  onSelect={(language) => onLanguageChange?.(language)}
                />
              )}
            </div>
            <button
              className={`sm:block md:hidden ${colors[color].text} w-[32px]`}
              onClick={() => setOpen(!open)}
            >
              <FontAwesomeIcon icon={open ? faXmark : faBars} />
            </button>
          </div>
        </Container>
      </div>
      {open && <TreeMenu className="flex md:hidden" pagesTree={menu} />}
    </div>
  );
}

function Logo({
  className,
  name,
  logoImgUrl,
}: {
  className?: string;
  name: string;
  logoImgUrl?: string;
}) {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {logoImgUrl ? (
        <NavLink className="h-full" to="/">
          <SafeImage
            alt="logo"
            className="h-full"
            fallback={fallback(name, false)}
            src={logoImgUrl}
          />
        </NavLink>
      ) : (
        fallback(name, true)
      )}
    </div>
  );

  function fallback(name: string, asLink: boolean) {
    return (
      <div className="flex items-center gap-2">
        {asLink ? (
          <NavLink to="/">
            <FontAwesomeIcon icon={faHouse} />
            <p className="font-bold">{name}</p>
          </NavLink>
        ) : (
          <>
            <FontAwesomeIcon icon={faHouse} />
            <p className="font-bold">{name}</p>
          </>
        )}
      </div>
    );
  }
}

function isActive(slug: string, pathname: string): boolean {
  const slugParts = slug.split("/");
  const pathnameParts = pathname.split("/");

  if (slugParts.length > pathnameParts.length) return false;

  return slugParts.every((part, index) => part === pathnameParts[index]);
}
